<template>
  <div>
    <div
      class="sf-title sf-below-1 tw-text-sm, tw-text-sf-gray-dark tw-leading-5"
    >
      {{ I18n.t('company_links.linked_companies') }}
    </div>
    <div class="tw-leading-[18px] tw-text-sf-gray-darker sf-pad-b-3">
      <span v-dompurify-html="I18n.t('company_links.description_html')" />
    </div>
  </div>
  <div v-if="availableCompanies.length" class="sf-pad-b-3">
    <div class="tw-bg-sf-neutral-lighter-1 sf-pad-2 sf-layer-rounded">
      <div class="tw-flex tw-gap-2 tw-items-center">
        <SfFixableContainer
          :fix-position="fixPosition"
          class="tw-flex-1 tw-min-w-0"
          data-walkthrough-id="available-companies"
          data-testid="available-companies"
        >
          <SfDropdownSearchable
            v-model="selectedCompanyId"
            :options="options"
            :placeholder="I18n.t('company_links.pick_company')"
            :search-placeholder="I18n.t('company_links.search_list')"
            data-testid="available-companies-filter-dropdown"
            dropdown-id="availableCompaniesDropdownFilter"
            :is-full-width="true"
            @opened="handleDropdownOpened"
            @closed="handleDropdownClosed"
          >
          </SfDropdownSearchable>
        </SfFixableContainer>

        <SfButtonDs
          variant="primary"
          size="sm"
          :disabled="isDisabled || isLoading"
          @click="handleCreateLink"
        >
          {{ I18n.t('company_links.add_company') }}
        </SfButtonDs>
      </div>
      <div
        v-if="selectedCompanyId && selectedCompanyName"
        class="sf-pad-t-1 tw-text-sf-gray-darker tw-leading-[18px]"
      >
        <span
          v-dompurify-html="
            I18n.t('company_links.new_link_description_html', {
              source_company_name: selectedCompanyName,
              target_company_name: currentCompanyName,
            })
          "
        ></span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { CreateCompanyLinkResponse } from '../types'

import { storeToRefs } from 'pinia'
import { ref, computed } from 'vue'

import { SfLog } from '@app/adapters/logger'
import { createCompanyLink } from '@app/application/company_links/api'
import { useCompanyLinksStore } from '@app/application/company_links/store/use-company-links'
import { useUserStore } from '@app/shared/pinia/user'
import { SfButtonDs } from '@silverfin/ui-components'
import SfDropdownSearchable from '@app/shared/vue/components/sf-dropdown/sf-dropdown-searchable.vue'
import SfFixableContainer from '@app/shared/vue/components/sf-fixable-container.vue'
import useI18n from '@app/shared/vue/composables/use-I18n'

const I18n = useI18n()

interface Props {
  currentUserId?: number
}

const fixPosition = ref(false)

const userStore = useUserStore()
const props = defineProps<Props>()
if (props.currentUserId) {
  userStore.setUserId(props.currentUserId)
}

const { userId } = storeToRefs(userStore)

const companyLinksStore = useCompanyLinksStore()

companyLinksStore.initAvailableCompanies()

const { availableCompanies, currentCompanyName } =
  storeToRefs(companyLinksStore)

const selectedCompanyId = ref<string | number | null>(null)

const selectedCompanyName = computed(() => {
  const selected = availableCompanies.value.find(
    (company) => company.id === selectedCompanyId.value
  )
  return selected ? selected.name : ''
})

const options = computed(() => {
  return availableCompanies.value.map((company) => ({
    label: company.name,
    value: company.id,
  }))
})

const isDisabled = computed(() => !selectedCompanyId.value)
const isLoading = ref(false)

const handleCreateLink = async () => {
  try {
    if (isLoading.value) return

    isLoading.value = true
    const companyId = selectedCompanyId.value
    selectedCompanyId.value = null

    const response = await createCompanyLink({
      sourceCompanyId: companyId as number,
      userId: userId.value as number,
    })
    const { companyLink, hasMatchingPeriod } =
      response as CreateCompanyLinkResponse

    companyLinksStore.addCompanyLink(companyLink, hasMatchingPeriod)

    isLoading.value = false
  } catch (error) {
    SfLog.warn('Failed to create a company link', {
      extra: {
        error: error,
      },
    })
    isLoading.value = false
  }
}

function handleDropdownOpened() {
  fixPosition.value = true
}

function handleDropdownClosed() {
  fixPosition.value = false
}
</script>
